import React, { Component } from 'react';
import { Alert, Button, Checkbox, Table } from 'antd';
import { ShieldCheck, Save, Lock, TriangleAlert, RotateCw } from 'lucide-react';
import Inherit from '../../core/inherit';
import withNavigate from '../../core/withNavigate';
import PermissionsController from './permissions.controller';
import { Layout, TableSkeleton, PageHeader, EmptyState } from '../common';
import { RoleHeader } from './elements';
import { t } from '../../company/labels';

/**
 * Permisos — admin-only console to edit which screens each admin-panel role can open and which
 * actions it can perform. Admin and Cliente are fixed; data scopes stay per role in code.
 */
class ShelfPermissions extends Inherit(PermissionsController, Component) {
    state = {
        loading: true,
        denied: false,
        error: null,
        saving: false,
        screens: [],
        actions: [],
        roles: [],
        draft: {},
        dirty: new Set()
    };

    roleHeader(r) {
        const { dirty, saving } = this.state;
        return <RoleHeader role={r} dirty={dirty} saving={saving} onReset={(role) => this.reset(role)} />;
    }

    get columns() {
        const { roles, draft } = this.state;
        return [
            {
                title: 'Permiso',
                key: 'label',
                fixed: 'left',
                width: 300,
                render: (_, row) =>
                    row.type === 'group' ? (
                        <span className="text-xs font-semibold uppercase tracking-wide text-surface-500">{row.label}</span>
                    ) : (
                        <div className="min-w-0">
                            <div className="text-sm text-surface-900">{row.label}</div>
                            {row.description && <div className="text-xs text-surface-500">{row.description}</div>}
                        </div>
                    )
            },
            ...roles.map((r) => ({
                title: this.roleHeader(r),
                key: r.role,
                align: 'center',
                width: 130,
                render: (_, row) => {
                    const d = draft[r.role];
                    if (!d) return null;
                    if (row.type === 'group') {
                        if (!r.editable) return null;
                        const kind = row.kind || 'screens';
                        const on = row.keys.filter((k) => d[kind].has(k)).length;
                        return (
                            <Checkbox
                                checked={on === row.keys.length}
                                indeterminate={on > 0 && on < row.keys.length}
                                onChange={(e) => this.toggleAll(r.role, kind, row.keys, e.target.checked)}
                            />
                        );
                    }
                    const checked = d[row.type].has(row.id);
                    return <Checkbox checked={checked} disabled={!r.editable} onChange={(e) => this.toggle(r.role, row.type, row.id, e.target.checked)} />;
                }
            }))
        ];
    }

    render() {
        const { loading, denied, error, saving, dirty } = this.state;
        const warnings = loading || denied || error ? [] : this.warnings;

        return (
            <Layout current="permissions">
                {this.props.contextHolder}

                <PageHeader
                    icon={<ShieldCheck />}
                    title="Permisos"
                    description="Qué pantallas y funciones del panel puede usar cada rol."
                    actions={
                        !denied &&
                        !error && (
                            <>
                                <Button onClick={() => this.discard()} disabled={!dirty.size || saving}>
                                    Descartar
                                </Button>
                                <Button type="primary" icon={<Save />} onClick={() => this.save()} disabled={!dirty.size} loading={saving}>
                                    Guardar cambios{dirty.size ? ` (${dirty.size})` : ''}
                                </Button>
                            </>
                        )
                    }
                />

                {denied ? (
                    <div className="shelf-card mx-auto mt-6 max-w-md">
                        <EmptyState
                            tone="danger"
                            icon={<Lock />}
                            title="Acceso restringido"
                            description="Solo los administradores pueden ver y editar los permisos."
                        />
                    </div>
                ) : error ? (
                    <div className="shelf-card mx-auto mt-6 max-w-md">
                        <EmptyState
                            tone="danger"
                            icon={<TriangleAlert />}
                            title="No se pudieron cargar los permisos"
                            description={error}
                            action={
                                <Button icon={<RotateCw />} onClick={() => this.load()}>
                                    Reintentar
                                </Button>
                            }
                        />
                    </div>
                ) : loading ? (
                    <TableSkeleton rows={12} columns={7} />
                ) : (
                    <>
                        <Alert
                            type="info"
                            showIcon
                            className="mb-4"
                            message="Cómo funciona"
                            description={
                                <ul className="mb-0 ml-4 list-disc space-y-0.5 text-surface-600">
                                    <li>Administrador siempre tiene todo (incluida esta pantalla) y no se puede editar.</li>
                                    <li>Cliente usa su menú fijo, limitado a su propia empresa.</li>
                                    <li>{t('permissionsScopeNote')}</li>
                                    <li>Los cambios aplican a los usuarios en cuanto navegan a otra pantalla o recargan (máx. ~30 s).</li>
                                </ul>
                            }
                        />
                        {warnings.length > 0 && (
                            <Alert
                                type="warning"
                                showIcon
                                className="mb-4"
                                message="Revisa antes de guardar"
                                description={
                                    <ul className="mb-0 ml-4 list-disc space-y-0.5">
                                        {warnings.map((w) => (
                                            <li key={w}>{w}</li>
                                        ))}
                                    </ul>
                                }
                            />
                        )}
                        <section className="shelf-card overflow-hidden">
                            <Table
                                columns={this.columns}
                                dataSource={this.rows}
                                pagination={false}
                                size="small"
                                sticky
                                scroll={{ x: 'max-content' }}
                                rowClassName={(row) => (row.type === 'group' ? 'bg-surface-50' : '')}
                            />
                        </section>
                    </>
                )}
            </Layout>
        );
    }
}

export default withNavigate(ShelfPermissions);
